import React, { useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Alert, AlertIcon, Box, Button, Heading, Spinner, Text, Tooltip, VStack } from '@chakra-ui/react';
import { Mutation, MutationAuthenticateSmsLoginLinkArgs, MutationApproveSmsLoginBrowserArgs, MutationSmsLoginBrowserApprovalArgs, SmsLoginResult } from '../../generated/graphql';
import { AUTHENTICATE_SMS_LOGIN_LINK, APPROVE_SMS_LOGIN_BROWSER, SMS_LOGIN_BROWSER_APPROVAL, GET_MY_CLIENT } from './queries';
import { clearSmsLinkCredential, getSmsLinkCredential } from './utils/smsLinkCredential';
import { useAuth } from '../../contexts/AuthContext';

export function SmsLinkCallback() {
  const apollo = useApolloClient();
  const { refreshAuth } = useAuth();
  const refresh = useRef(refreshAuth);
  refresh.current = refreshAuth;
  const token = useRef(getSmsLinkCredential());
  const currentSession = useRef(!!localStorage.getItem('auth_token'));
  const exchange = useRef<Promise<SmsLoginResult>>();
  const handled = useRef(false);
  const destination = useRef('/');
  const [label, setLabel] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);
  function continueHere() { clearSmsLinkCredential(); window.location.replace(destination.current); }
  useEffect(() => {
    let stopped = false;
    async function approval() {
      const { data } = await apollo.mutate<Pick<Mutation, 'smsLoginBrowserApproval'>, MutationSmsLoginBrowserApprovalArgs>({ mutation: SMS_LOGIN_BROWSER_APPROVAL, variables: { token: token.current! } });
      if (stopped) return;
      const pending = data?.smsLoginBrowserApproval;
      if (pending && pending !== localStorage.getItem('sms_login_browser_label')) { setLabel(pending); setBusy(false); return; }
      if (currentSession.current) { setBusy(false); return; }
      continueHere();
    }
    if (!token.current) { setError('This SMS link is missing. Reopen the message or request a new link.'); setBusy(false); return; }
    if (currentSession.current) {
      void approval().catch(() => { if (!stopped) { setError('Could not check the waiting browser. Reopen the SMS to retry.'); setBusy(false); } });
      return () => { stopped = true; };
    }
    if (!exchange.current) exchange.current = apollo.mutate<Pick<Mutation, 'authenticateSmsLoginLink'>, MutationAuthenticateSmsLoginLinkArgs>({ mutation: AUTHENTICATE_SMS_LOGIN_LINK, variables: { token: token.current }, fetchPolicy: 'no-cache' }).then(({ data }) => {
      if (!data?.authenticateSmsLoginLink?.auth?.token) throw new Error('Sign-in did not complete. Request a new SMS link.');
      return data.authenticateSmsLoginLink;
    });
    void exchange.current.then(async result => {
      if (stopped || handled.current) return;
      handled.current = true;
      if (!localStorage.getItem('auth_token')) localStorage.setItem('auth_token', result.auth!.token!);
      const path = result.returnPath || '/';
      const url = new URL(path, window.location.origin);
      destination.current = path.startsWith('/') && !path.startsWith('//') && url.origin === window.location.origin ? path : '/';
      await apollo.clearStore();
      await apollo.query({ query: GET_MY_CLIENT, fetchPolicy: 'network-only' });
      refresh.current();
      await approval();
    }).catch(err => { if (!stopped) { setError(err instanceof Error ? err.message : 'Sign-in failed. Request a new SMS link.'); setBusy(false); } });
    return () => { stopped = true; };
  }, [apollo]);
  async function approve() {
    setBusy(true); setError('');
    try {
      const { data } = await apollo.mutate<Pick<Mutation, 'approveSmsLoginBrowser'>, MutationApproveSmsLoginBrowserArgs>({ mutation: APPROVE_SMS_LOGIN_BROWSER, variables: { token: token.current!, label } });
      if (!data?.approveSmsLoginBrowser) throw new Error('This browser request is no longer available.');
      continueHere();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not approve this browser.'); setBusy(false); }
  }
  return <Box minH="100dvh" display="flex" alignItems="center" p={6}><VStack align="stretch" spacing={6} maxW="430px" w="full" mx="auto">
    <Heading size="lg">{label ? 'Also sign in your other browser?' : error ? 'Sign-in needs attention' : currentSession.current ? 'You’re already signed in' : 'Signing you in…'}</Heading>
    {error && <Alert status="error"><AlertIcon />{error}</Alert>}
    {label ? <>
      <Text>You’re signed in here. Approve only if you requested this SMS yourself and your waiting browser shows <strong>{label}</strong>.</Text>
      <Tooltip label="Allow the waiting browser with this reference to sign in to your account."><Button colorScheme="green" isLoading={busy} onClick={approve}>Approve waiting browser</Button></Tooltip>
      <Tooltip label="Continue here without allowing another browser to sign in."><Button variant="ghost" onClick={continueHere}>Continue here only</Button></Tooltip>
    </> : busy ? <Spinner label="Signing you in" alignSelf="center" /> : <>
      {currentSession.current && <Text>Continue with your current account. To use a different account, sign out first and reopen the SMS.</Text>}
      <Tooltip label="Continue with your signed-in account, or return to sign-in to request another link."><Button onClick={() => { clearSmsLinkCredential(); window.location.replace(localStorage.getItem('auth_token') ? destination.current : '/login'); }}>Continue</Button></Tooltip>
    </>}
  </VStack></Box>;
}
